/**
 * Curso compuesto (Esteban, 2026-09-18): a course such as Hotelería or Belleza General is defined by BLOCKS.
 * Each block says in which module (of another course of the same campus) the classes are taken and how many
 * sessions it is worth (one session = 4 h). Blocks have no fixed order; the student starts where she wants.
 * Corrected model (owner): the enrollment belongs to the COMPOSITE course (that is what bills, via its plan de
 * pagos) but its schedule is a real schedule of the block being taken right now — there is no "Hotelería schedule".
 * Moving to the next block moves the enrollment to that block's schedule (schedule change without re-billing,
 * with history); "En paralelo" blocks are an additional authorized schedule instead.
 */

export const BLOCK_KIND = {
	/** Must be taken. */
	REQUIRED: 1,
	/** Blocks sharing an `electiveGroup`: the student picks one of them. */
	ELECTIVE: 2,
	/** Runs alongside the rest (e.g. Inglés, 48 sessions). */
	PARALLEL: 3,
	/** Paid apart. */
	OPTIONAL: 4
} as const;

export type BlockKindId = typeof BLOCK_KIND[keyof typeof BLOCK_KIND];

export const BLOCK_KIND_LABELS: { [kindId: number]: string } = {
	[BLOCK_KIND.REQUIRED]: "Obligatorio",
	[BLOCK_KIND.ELECTIVE]: "A elegir",
	[BLOCK_KIND.PARALLEL]: "En paralelo",
	[BLOCK_KIND.OPTIONAL]: "Opcional"
};

export const BLOCK_STATUS = {
	PENDING: 1,
	IN_PROGRESS: 2,
	COMPLETE: 3
} as const;

export interface BlockDTO {
	id: number;
	courseId: number;
	name: string;
	ordering: number;
	kindId: BlockKindId;
	kindLabel: string;
	electiveGroup: string | null;
	sessions: number;
	targetLevelId: number | null;
	targetLevelName: string | null;
	targetCourseName: string | null;
	/** Course that owns `targetLevelId`. Not always sent: resolve through `_blockTargets` when missing. */
	targetCourseId?: number | null;
}

/** GET .../courses/{courseId}/_blocks */
export interface CourseBlocksDTO {
	course: { id: number; name: string; code: string };
	blocks: BlockDTO[];
}

/** GET .../courses/_blockTargets — active modules of the campus a block can point to. */
export interface BlockTargetDTO {
	levelId: number;
	levelName: string;
	courseId: number;
	courseName: string;
	courseCode: string;
}

/** POST / PUT .../courses/{courseId}/_blocks[/{blockId}] */
export interface BlockBody {
	name: string;
	kindId: BlockKindId;
	electiveGroup?: string | null;
	sessions: number;
	targetLevelId: number;
	ordering?: number;
}

export interface BlockScheduleOptionDTO {
	id: number;
	name: string;
	weeklyHours: number;
	mentorName: string | null;
}

export interface EnrollmentBlockDTO {
	block: BlockDTO;
	scheduleId: number | null;
	scheduleName: string | null;
	/** This block's schedule is the enrollment's current schedule ("Cursando ahora"). */
	isCurrent: boolean;
	statusId: 1 | 2 | 3;
	statusLabel: string;
	hoursTaken: number;
	hoursTarget: number;
	sessionsTaken: number;
	options: BlockScheduleOptionDTO[];
}

/** GET / PUT .../students/{studentId}/enrollments/{enrollmentId}/_blocks */
export interface EnrollmentBlocksDTO {
	isComposite: boolean;
	courseName: string;
	blocks: EnrollmentBlockDTO[];
	totals: { hoursTaken: number; hoursTarget: number };
}

/** Schedule of a block's target module offered when enrolling into a composite course. */
export interface CompositeBlockScheduleDTO {
	id: number;
	name: string;
	levelId: number;
	periodName: string;
	startDate: string | null;
	mentorName: string | null;
	weeklyHours: number;
	enrolled: number;
	limit: number;
}

export interface CompositeCourseBillingDTO {
	billableMonths: number;
	monthlyPrice: number;
	subscriptionPrice: number;
	confirmed: boolean;
}

/** GET .../courses/_composite — composite courses of the campus, each block with its available schedules. */
export interface CompositeCourseDTO {
	id: number;
	name: string;
	code: string;
	billing: CompositeCourseBillingDTO | null;
	blocks: (BlockDTO & { schedules: CompositeBlockScheduleDTO[] })[];
}
